import isEqual from 'lodash/isEqual'
import {
  BannerClickData,
  BannerClickEvent,
  BannerImpressionData,
  BannerImpressionEvent,
  GtmBanner,
  SpecialBannerClickData,
  SpecialBannerImpressionData,
} from './banners'
import {
  BaseProductData,
  BaseProductDataArticles,
  GtmProduct,
  ProductClickData,
  ProductClickEvent,
  ProductDetailData,
  ProductDetailEvent,
  ProductImpressionData,
  ProductImpressionEvent,
} from './products'
import {
  AddToCartData,
  AddToCartEvent,
  RemoveFromCartData,
  RemoveFromCartEvent,
} from './cart'

interface GtmPushFunc {
  (obj: object): any
}

export interface GtmPlugin {
  init: Function
  push: GtmPushFunc
}

export const GTM_MAIN_EVENT_NAME = 'gtm-event'
export const GTM_PAGEVIEW_EVENT_NAME = 'gtm-pageview'
export const GTM_EVENT_FIRE_EVENT_NAME = 'gtm-eventFire'
// https://github.com/nuxt-community/gtm-module/blob/master/lib/plugin.js#L71
export const GTM_PAGEVIEW_DELAY = 250
export const GTM_PAGEVIEW_DELAY_TRACK = 800

export enum GtmChannelFlag {
  CAMPAIGN = 'category', // this is not mistake :)
  CATEGORY = 'category',
  PARTNER = 'category',
  BRAND = 'category',
  AUTHOR = 'category',
  LICENCE = 'category',
  DETAIL = 'detail',
  HOMEPAGE = 'home',
  SEARCH = 'searchresults',
  OTHER = 'other',
}

export enum GtmEventNames {
  PAGE_VIEW = 'virtualPageview',
  ZERO_EVENT = 'zeroEvent',
  PRODUCT_IMPRESS = 'evImpress',
  PRODUCT_CLICK = 'productClick',
  PRODUCT_DETAIL = 'productDetail',
  ADD_TO_CART = 'addToCart',
  REMOVE_FROM_CART = 'removeFromCart',
  INT_PROMO = 'intPromo',
  INT_PROMO_CLICK = 'intPromoClick',
  EVENT_FIRE = 'eventFire',
  ACTIVE_AB_TESTS_COOKIE = 'mgTestsActiveUpdate',
}

export enum GtmEventTypes {
  BannerClick = 'BannerClick',
  BannerImpression = 'BannerImpression',
  ProductClick = 'ProductClick',
  ProductImpression = 'ProductImpression',
  AddToCart = 'AddToCart',
  RemoveFromCart = 'RemoveFromCart',
  ProductDetail = 'ProductDetail',
  EventFire = 'EventFire',
  /* These are examples of events that we might track, we need to get an actual list of events to be tracked
  CancelOrder = 'CancelOrder',
  DownloadInvoice = 'DownloadInvoice',
  BuyAgain = 'BuyAgain',
  AddShippingAddress = 'AddShippingAddress',
  */
}

export enum WebKCGtmEventLabels {
  COPY_ORDER_ID = 'order id copy',
  CANCEL_ORDER = 'cancellation',
  OPEN_DELIVERY_DETAIL = 'shipment detail',
  OPEN_ORDER_DETAIL = 'order detail',
  DOWNLOAD_INVOICE = 'dowload invoice',
  CLAIM_ORDER = 'rma',
  RETURN_ORDER = 'return',
  VIEW_DIGITAL_LICENCE = 'view licence',
  INIT_CHANGE_PAYMENT = 'change payment',
  FINISH_CHANGE_PAYMENT = 'change payment done',
  NAVIGATION = 'navigation',

  INIT_CHANGE_PASSWORD = 'change password',
  FINISH_CHANGE_PASSWORD = 'change password done',
  INIT_CHANGE_EMAIL = 'change email',
  FINISH_CHANGE_EMAIL = 'change email done',
  INIT_CHANGE_USER_DATA = 'change data',
  FINISH_CHANGE_USER_DATA = 'change data done',

  EDIT_ADDRESS = 'edit address',
  DELETE_ADDRESS = 'delete address',
  ADD_ADDRESS = 'add address',

  DELETE_ACCOUNT = 'Delete account',

  MARKETING_EMAIL_ALLOW = 'email - allow',
  MARKETING_EMAIL_STOP = 'email - stop',
  MARKETING_OTHERS_ALLOW = 'others - allow',
  MARKETING_OTHERS_STOP = 'others - stop',
  REVIEW_ALLOW = 'review - allow',
  REVIEW_STOP = 'review - stop',
  MARKETING_3P_ALLOW = 'marketing 3p - allow',
  MARKETING_3P_STOP = 'marketing 3p - stop',
  PERSONAL_DATA_3P_ALLOW = 'personal data 3p - allow',
  PERSONAL_DATA_3P_STOP = 'personal data 3p - stop',

  // SMART
  CHECKED = 'checked',
  UNCHECKED = 'unchecked',
  SMART_MORE_INFO_LINK = 'Kompletni podminky',
  SMART_PERSONAL_DATA_CONDITIONS_LINK = 'Kompletni podminky',

  DELETE_WISHLIST = 'delete wishlist',
  RENAME_WISHLIST = 'rename wishlist',
  WISHLIST_DETAIL = 'wishlist detail',
  REMOVE_WISHLIST_ITEM = 'remove wishlist item',
  CREATE_WISHLIST = 'create wishlist',
  SHARE_WISHLIST = 'share wishlist',
  CANCEL_SHARE_WISHLIST = 'share wishlist cancel',

  CLUB_UNSUBSCRIBE = 'club unsubscribe',
  CLUB_EDIT = 'club edit',
  CLUB_DETAIL = 'club detail',
  CLUB_SAVE_FORM = 'club save form',

  SHOW_RMA_AUTHORIZED_SERVICE = 'show rma authorized service',
}

export enum WebKCGtmEventActions {
  BUTTON_CLICKS = 'Button clicks',
  REMOVE_SAVED_CARD = 'Remove saved card',
  MARKETING_NEWS = 'Marketing news',
  // SMART
  SMART_CONSENT_1 = 'Purple checkbox consent-1',
  SMART_CONSENT_2 = 'Purple checkbox consent-2',
  SMART_CONSENT_3 = 'Purple checkbox consent-3',
  SMART_PURPLE_BOX = 'Purple box',
  SMART_MODAL = 'Mall Smart modal',
}

export enum WebKCGtmEventCategories {
  KC = 'Client center',
  SMART = 'Smart',
}

export type GtmEventData =
  | BannerClickData
  | BannerImpressionData
  | SpecialBannerClickData
  | SpecialBannerImpressionData
  | ProductImpressionData
  | ProductClickData
  | ProductDetailData
  | AddToCartData
  | RemoveFromCartData

export interface GtmEvent extends Event {
  detail: {
    data: GtmEventData
    eventType: GtmEventTypes
  }
}

export type ProductOptions = Omit<BaseProductDataArticles, 'id'>

type BannerDataResolver = (
  evt: BannerImpressionEvent | BannerClickEvent
) => GtmBanner

export type InspectElement = (
  evt: BannerImpressionEvent | ProductImpressionEvent,
  inspectData: object
) => void

type ProductDataResolver = (
  evt:
    | ProductClickEvent
    | AddToCartEvent
    | RemoveFromCartEvent
    | ProductDetailEvent
) => Promise<GtmProduct[]>

interface ActionResolverArgs {
  evt: GtmEvent
  gtm: GtmPlugin
  bannerDataResolver: BannerDataResolver
  bannerVisibilityQueue: GtmBanner[]
  productDataResolver: ProductDataResolver
  productVisibilityQueue: Record<string, ProductOptions>
  countVisiblePage: number
  pageType: GtmChannelFlag
  inspectElement: InspectElement
  pageTypeByRouteName: (
    routeName: BaseProductData['routeName']
  ) => GtmChannelFlag
}

const ProductImpressionVisibleElement: Record<string, boolean> = {}

export const actionResolver = (args: ActionResolverArgs) => {
  const {
    evt: {
      detail: { eventType },
    },
    gtm,
    bannerDataResolver,
    bannerVisibilityQueue,
    productDataResolver,
    productVisibilityQueue,
    countVisiblePage,
    pageType,
    inspectElement,
    pageTypeByRouteName,
  } = args
  const actionsDefinition: Record<GtmEventTypes, Function> = {
    [GtmEventTypes.BannerClick]: (evt: BannerClickEvent) => {
      const { bannerId, ...data } = bannerDataResolver(evt)
      const promotionObj = { ...data, id: pageType }

      gtm.push({
        ecommerce: {
          promoClick: {
            promotions: [promotionObj],
          },
        },
        event: GtmEventNames.INT_PROMO_CLICK,
      })
    },

    [GtmEventTypes.BannerImpression]: (evt: BannerImpressionEvent) => {
      const data = bannerDataResolver(evt)
      const promotionObj = { ...data, id: pageType }

      const isEqualBanner = bannerVisibilityQueue.some((banner) =>
        isEqual(banner, promotionObj)
      )

      if (!isEqualBanner) {
        bannerVisibilityQueue.push(promotionObj)
        inspectElement(evt, promotionObj)
      }
    },
    [GtmEventTypes.ProductClick]: async (evt: ProductClickEvent) => {
      const product = await productDataResolver(evt)

      const { routeName } = evt.detail.data

      const { list } = evt.detail.data.articles[0]
      const pageTypeList = pageTypeByRouteName(routeName)

      gtm.push({
        pageTypeList,
        ecommerce: {
          click: {
            actionField: {
              action: 'click',
              list,
            },
            products: product,
          },
        },
        event: GtmEventNames.PRODUCT_CLICK,
      })
    },
    [GtmEventTypes.ProductDetail]: async (evt: ProductDetailEvent) => {
      const products = await productDataResolver(evt)

      gtm.push({
        ecommerce: {
          detail: {
            products,
          },
        },
        event: GtmEventNames.PRODUCT_DETAIL,
      })
    },
    [GtmEventTypes.ProductImpression]: (evt: ProductImpressionEvent) => {
      const key = JSON.stringify({
        ...evt.detail.data,
        countVisiblePage,
      })

      const { routeName } = evt.detail.data

      if (!ProductImpressionVisibleElement[key]) {
        evt.detail.data.articles.forEach((article) => {
          const pageTypeList = pageTypeByRouteName(routeName)

          const optionsWithPageType = { ...article, pageTypeList }

          productVisibilityQueue[article.id] = optionsWithPageType
          inspectElement(evt, optionsWithPageType)
        })
      }

      ProductImpressionVisibleElement[key] = true
    },
    [GtmEventTypes.AddToCart]: async (evt: AddToCartEvent) => {
      const products = await productDataResolver(evt)
      gtm.push({
        ecommerce: {
          add: {
            products,
          },
        },
        event: GtmEventNames.ADD_TO_CART,
      })
    },
    [GtmEventTypes.RemoveFromCart]: async (evt: RemoveFromCartEvent) => {
      const products = await productDataResolver(evt)

      gtm.push({
        ecommerce: {
          remove: {
            products,
          },
        },
        event: GtmEventNames.REMOVE_FROM_CART,
      })
    },
    [GtmEventTypes.EventFire]: (evt: any) => {
      console.error(
        'This should never happen: ' +
          GtmEventTypes.EventFire +
          ' in eventListener for ' +
          GTM_MAIN_EVENT_NAME,
        evt
      )
    },
  }

  return actionsDefinition[eventType]
}

export interface EventFireData {
  eventCategory: string
  eventAction: string
  eventLabel: string
  eventValue: number
  eventNonInteractionFlag: boolean
}

export const gtmPushEventFire = (gtm: GtmPlugin, data: EventFireData) => {
  gtm.push({
    event: GtmEventNames.EVENT_FIRE,
    ...data,
    eventCallback: () => {
      gtm.push({
        eventAction: undefined,
        eventCategory: undefined,
        eventLabel: undefined,
        eventValue: undefined,
      })
    },
  })
}
